import { ECloneWorldMarkerStoryCloneType } from '__generated__/globalTypes';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { useAccess } from 'hooks/access/useAccess';
import { useCloneWorldMarker } from 'hooks/marker/useCloneWorldMarker';
import { useReorderWorldMarker } from 'hooks/marker/useReorderWorldMarker';
import _ from 'lodash';
import { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IWorldMarker;
}

export const MarkerControlsSection = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ctxEdit = useContext(CtxEdit);
    const cloneMarker = useCloneWorldMarker();
    const reorderMarkers = useReorderWorldMarker(props.marker);
    const access = useAccess();

    function onDeleteClicked() {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: 'Do you want to delete this marker?',
                onConfirm: () => {
                    ctxEdit.removeWorldMarkers([props.marker.id]);
                }
            } as IConfirmActionModal
        });
    }

    const smallestOrder = _.minBy(ctxEdit.worldMarkers, 'order')?.order;
    const highestOrder = _.maxBy(ctxEdit.worldMarkers, 'order')?.order;

    const showUseInGuideProgram =
        access.isExperimental &&
        !(props.marker.elementType === 'v2/icon' || props.marker.elementType === 'indoor_stand');

    return (
        <S_MarkerControlsSection className={props.className}>
            <SidebarSection title={Localization.CONTROLS} icon={{ iconName: 'joystick' }}>
                <SidebarContent withHover withTopPadding>
                    {showUseInGuideProgram && (
                        <SidebarSectionRow title={Localization.USE_IN_GUIDE}>
                            {props.marker.isRoomMarker && (
                                <TextValue
                                    value={props.marker.roomName}
                                    placeholder={Localization.LOCATION_NAME}
                                    onChange={(value) => {
                                        ctxEdit.updateWorldMarker(props.marker.id, {
                                            roomName: value,
                                            isRoomMarker: true
                                        });
                                    }}
                                />
                            )}
                            <CheckValue
                                value={props.marker.isRoomMarker}
                                label=""
                                onChange={(value) => {
                                    ctxEdit.updateWorldMarker(props.marker.id, {
                                        isRoomMarker: value
                                    });
                                }}
                            />
                        </SidebarSectionRow>
                    )}
                    <SidebarSectionRow title={Localization.CLONE}>
                        <ActionButton
                            icon={{ iconName: 'clone' }}
                            onClick={() => {
                                cloneMarker(props.marker.id, {
                                    cloneInteractivity: ECloneWorldMarkerStoryCloneType.EMPTY
                                });
                            }}
                        />
                    </SidebarSectionRow>
                    {ctxEdit.worldMarkers?.length > 1 && (
                        <SidebarSectionRow title={Localization.ORDER}>
                            {props.marker?.order !== highestOrder && (
                                <ActionButton
                                    icon={{ iconName: 'bring-forward' }}
                                    onClick={reorderMarkers.bringForward}
                                    text={Localization.TOP}
                                />
                            )}
                            {props.marker?.order !== smallestOrder && (
                                <ActionButton
                                    icon={{ iconName: 'send-backward' }}
                                    onClick={reorderMarkers.bringBackward}
                                    text={Localization.BOTTOM}
                                />
                            )}
                        </SidebarSectionRow>
                    )}
                    <SidebarSectionRow title={Localization.DELETE}>
                        <ActionButton icon={{ iconName: 'trash' }} onClick={onDeleteClicked} />
                    </SidebarSectionRow>
                </SidebarContent>
            </SidebarSection>
        </S_MarkerControlsSection>
    );
};

const S_MarkerControlsSection = styled.div``;
